import { state } from '../state';
import { formatTime } from '../ui';

interface JsPDF {
  internal: { pageSize: { getWidth(): number; getHeight(): number } };
  setFont(font: string, style: string): void;
  setFontSize(size: number): void;
  setTextColor(r: number, g: number, b: number): void;
  setDrawColor(r: number, g: number, b: number): void;
  setLineWidth(width: number): void;
  setFillColor(r: number, g: number, b: number): void;
  text(text: string | string[], x: number, y: number): void;
  line(x1: number, y1: number, x2: number, y2: number): void;
  roundedRect(
    x: number,
    y: number,
    w: number,
    h: number,
    rx: number,
    ry: number,
    style: string
  ): void;
  splitTextToSize(text: string, maxWidth: number): string[];
  getTextWidth(text: string): number;
  addPage(): void;
  save(filename: string): void;
}

declare global {
  interface Window {
    jspdf: { jsPDF: new () => JsPDF };
  }
}

const PDF_SPEAKER_COLORS: Array<[number, number, number]> = [
  [31, 16, 142],
  [107, 56, 212],
  [59, 130, 246],
  [16, 185, 129],
  [236, 72, 153],
];

export function exportPdf(includeTimestamps: boolean): void {
  if (state.paragraphs.length === 0) return;
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF();

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(31, 16, 142);
  doc.text('Scribe — Transcripción', margin, y);
  y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 140);
  doc.text(new Date().toLocaleString('es-ES'), margin, y);
  y += 4;

  doc.setDrawColor(200, 196, 213);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;

  for (const para of state.paragraphs) {
    const colorIdx = para.speaker % PDF_SPEAKER_COLORS.length;
    const color = PDF_SPEAKER_COLORS[colorIdx];
    const name = state.speakerMap[para.speaker] ?? `Speaker ${para.speaker}`;
    const timeStr = includeTimestamps ? `[${formatTime(para.start)}]  ` : '';

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const wrappedText = doc.splitTextToSize(para.text, contentWidth - 4);
    const blockHeight = 7 + wrappedText.length * 5 + 8;

    if (y + blockHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }

    doc.setFillColor(color[0], color[1], color[2]);
    doc.roundedRect(margin, y, 3, blockHeight - 4, 1.5, 1.5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(color[0], color[1], color[2]);
    doc.text(name, margin + 7, y + 5);

    if (includeTimestamps) {
      const nameWidth = doc.getTextWidth(name);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 160);
      doc.text(timeStr, margin + 7 + nameWidth + 3, y + 5);
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 40);
    doc.text(wrappedText, margin + 7, y + 12);

    y += blockHeight;
  }

  doc.save('Transcripcion_Scribe.pdf');
}
